import {
  Center,
  ActionIcon,
  useMantineColorScheme,
  ThemeIcon,
} from "@mantine/core"
import { IconArrowsDoubleSwNe, IconSum } from "@tabler/icons-react"
import css from "./matrix-css-grid.module.css"
import { DayBorderCell } from "./cells/day-border-cell"
import { useUserLocalStorage } from "@/routes/app/-components/use-user-local-storage"

export type MatrixOrientation = "normal" | "transpose"

export type MatrixCssGridProps<
  X extends { width: number },
  XGroup extends { span: number },
  Y,
> = {
  dataX: X[]
  isFirstInGroup: (x: X) => boolean
  getGroup(x: X): XGroup | null
  dataXGroups: XGroup[]
  xHeader: (x: X, orientation: MatrixOrientation, i: number) => React.ReactNode
  xHeaderGroup: (
    xg: XGroup,
    orientation: MatrixOrientation,
    i: number
  ) => React.ReactNode
  yHeader: (y: Y, orientation: MatrixOrientation, i: number) => React.ReactNode
  dataY: Y[]
  noDataX: React.ReactNode
  noDataY: React.ReactNode
  renderCell: (
    x: X,
    y: Y,
    ix: number,
    iy: number,
    orientation: MatrixOrientation
  ) => React.ReactNode
}

type XGroupBase = { span: number }

export function MatrixCssGrid<
  X extends { width: number },
  XGroup extends XGroupBase,
  Y,
>(props: MatrixCssGridProps<X, XGroup, Y>) {
  const [orientation, setOrientation] = useUserLocalStorage<MatrixOrientation>({
    key: "duty-scheduling-matrix-orientation",
    defaultValue: "normal",
  })
  const data = props
  const { colorScheme } = useMantineColorScheme()
  const xCount = data.dataX.length
  const xWidths = data.dataX.map((v) => v.width + "px").join(" ")
  const yCount = data.dataY.length
  return (
    <>
      {data.dataX.length === 0 ? props.noDataX : null}
      {data.dataY.length === 0 ? props.noDataY : null}
      <div
        className={css.grid}
        style={{
          gridAutoFlow: orientation === "normal" ? "row" : "column",
          gridTemplateColumns:
            orientation === "normal"
              ? `75px ${xWidths} `
              : `auto 90px repeat(${yCount},64px)`,
          gridTemplateRows:
            orientation === "transpose"
              ? `auto repeat(${xCount}, auto)`
              : undefined,
        }}
      >
        <Center
          style={{
            zIndex: 100,
            position: "sticky",
            top: 0,
            left: 0,
          }}
          p={4}
          onClick={() => {
            setOrientation(orientation === "normal" ? "transpose" : "normal")
          }}
        >
          <ActionIcon
            size="xs"
            variant={colorScheme === "dark" ? "light" : "light"}
            color={colorScheme === "dark" ? "gray" : "gray"}
          >
            <IconArrowsDoubleSwNe />
          </ActionIcon>
        </Center>
        {data.dataX.map((x, i) => {
          const group = props.getGroup(x)
          if (group == null) {
            return (
              <div
                key={i}
                style={{
                  zIndex: 100,
                  position: "sticky",
                  top: orientation === "normal" ? 0 : undefined,
                  left: orientation === "transpose" ? 0 : undefined,
                }}
              >
                <ThemeIcon variant="transparent" c={"dimmed"}>
                  <IconSum size={14} />
                </ThemeIcon>
              </div>
            )
          } else {
            if (props.isFirstInGroup(x)) {
              return (
                <>
                  <DayBorderCell key={`day-${i}`} />
                  <div
                    key={i}
                    style={{
                      position: "sticky",
                      top: orientation === "normal" ? 0 : undefined,
                      left: orientation === "transpose" ? 0 : undefined,
                      zIndex: 100,
                      gridColumn:
                        orientation == "normal"
                          ? `span ${group.span}`
                          : undefined,
                      gridRow:
                        orientation === "transpose"
                          ? `span ${group.span}`
                          : undefined,
                    }}
                  >
                    {props.xHeaderGroup(group, orientation, i)}
                  </div>
                </>
              )
            } else {
              return null
            }
          }
        })}
        <div
          style={{
            zIndex: 100,
            position: "sticky",
            top: orientation === "transpose" ? 0 : 28,
            left: orientation === "normal" ? 0 : 28,
          }}
        />
        {data.dataX.map((v, i) => data.xHeader(v, orientation, i))}
        {data.dataY.map((y, iy) => (
          <>
            {data.yHeader(y, orientation, iy)}
            {data.dataX.map((x, ix) =>
              data.renderCell(x, y, ix, iy, orientation)
            )}
          </>
        ))}
      </div>
    </>
  )
}
